import { ChatDetailsResponse } from '@choco-admin/types';
import { getInitials } from '@choco-admin/utils';
import { Chip, Avatar, Button, Switch, Spinner } from '@heroui/react';
import React, { useCallback, useMemo, useState } from 'react';
import {
  FiTruck,
  FiExternalLink,
  FiBell,
  FiMail,
  FiEdit,
  FiPlus,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

import {
  EntitySearchModal,
  type EntitySearchOption,
} from '../../../../../../../components/FormComponents';
import { useNotification } from '../../../../../../../contexts/NotificationContext';
import { useAssignChatSupplier } from '../../../../../hooks/useAssignChatSupplier';
import { useChatNotificationPreferences } from '../../../../../hooks/useChatNotificationPreferences';
import { useSearchSuppliersForChat } from '../../../../../hooks/useSearchSuppliersForChat';
import { useUpdateChatNotificationPreferences } from '../../../../../hooks/useUpdateChatNotificationPreferences';
import { getSupplierProfileRoute } from '../../../../../utils/editChatMapper';
import { canAssignDistributor } from '../../../../../utils/requestedChatLifecycle';
import { useChatPageContext } from '../../../contexts';

interface SupplierSectionProps {
  chatData: ChatDetailsResponse;
  chatId: string;
}

export const SupplierSection: React.FC<SupplierSectionProps> = ({
  chatData,
  chatId,
}) => {
  const { chatData: contextChatData } = useChatPageContext();
  // Read every distributor-card field from the SAME source. `contextChatData`
  // is the reconciled truth (immediate mutation writes + adopted GetChatData
  // refetches, see ChatPageContextProvider); it falls back to the prop only
  // before the provider seeds. Mixing prop and context reads in one component
  // is exactly what left the card showing a stale "No distributor assigned"
  // after an assign refetch while `canAssign` read the fresh prop.
  const currentChatData = contextChatData || chatData;
  const supplier = currentChatData.supplierEntity;
  const supplierProfileRoute = getSupplierProfileRoute(currentChatData);
  const navigate = useNavigate();
  const { addNotification } = useNotification();

  // Legacy parity: a distributor can only be assigned/changed while the chat
  // is `requested` (see requestedChatLifecycle.canAssignDistributor). Active
  // chats cannot change their distributor through this workflow.
  const canAssign = canAssignDistributor(currentChatData.status);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);

  const {
    suppliers,
    isLoading: isSearchingSuppliers,
    searchSuppliers,
  } = useSearchSuppliersForChat();

  const { assignSupplier, isAssigning } = useAssignChatSupplier({
    onSuccess: () => setIsAssignModalOpen(false),
  });

  const handleAssignSupplier = useCallback(
    async (selected: EntitySearchOption) => {
      if (!selected.id) return;
      await assignSupplier({ chatId, supplierEntityId: selected.id });
    },
    [assignSupplier, chatId]
  );

  // Order-notification rows cover EVERY distributor-side recipient, INCLUDING
  // the supplier bot user. The bot user's address is the distributor's
  // general/primary order email (the highlighted "Supplier" checkbox in legacy
  // Admin), and the backend suppresses it per chat by its user id in
  // `deselectedUsersList` — exactly like any other user — so CS must be able to
  // switch it off here. (#122 still excludes the bot user from the removable
  // participant list + add-user selectors; it just isn't a removable contact.)
  // The bot user (primary) is sorted first so its general email is the top row.
  const notificationUsers = useMemo(() => {
    const supplierSide = currentChatData.users.filter((user) => user.supplier);
    return [...supplierSide].sort(
      (a, b) => Number(Boolean(b.botUser)) - Number(Boolean(a.botUser))
    );
  }, [currentChatData.users]);

  const {
    notificationPreferences,
    isLoading: isPrefsLoading,
    refetch: refetchPrefs,
  } = useChatNotificationPreferences({ chatId });

  // Track which user's toggle is mid-flight so we can show a per-row spinner
  // while disabling the rest (each update re-reads the current list first).
  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);

  const { updateNotificationPreferences, isUpdating } =
    useUpdateChatNotificationPreferences({
      onSuccess: () => {
        addNotification({
          title: 'Notification Preferences Updated',
          message: 'Order notifications have been updated for this chat.',
          type: 'success',
        });
        refetchPrefs().catch(
          // Refetch failure is non-critical; the mutation already succeeded
          () => undefined
        );
      },
      onError: (error) => {
        addNotification({
          title: 'Update Failed',
          message: error.message,
          type: 'error',
        });
      },
    });

  // Each toggle is derived from that user's membership in deselectedUsersList:
  // present => notifications disabled for that user; absent => enabled. One
  // user's preference never depends on another's.
  const deselectedList = useMemo(
    () => notificationPreferences?.newOrder?.deselectedUsersList ?? [],
    [notificationPreferences]
  );

  const handleNotificationChange = useCallback(
    async (userId: string, isSelected: boolean) => {
      setUpdatingUserId(userId);
      try {
        await updateNotificationPreferences({
          chatId,
          userId,
          isEnabled: isSelected,
        });
      } catch {
        // onError already surfaced a toast; swallow so the row re-enables.
      } finally {
        setUpdatingUserId(null);
      }
    },
    [chatId, updateNotificationPreferences]
  );

  const handleSupplierClick = () => {
    if (supplierProfileRoute) {
      navigate(supplierProfileRoute);
    }
  };

  // The primary row shows the distributor's order emails; the bot user's own
  // email is the legacy field and is often empty.
  const orderEmails = currentChatData.supplierOrderEmails ?? [];
  const getRowEmail = (user: { botUser?: boolean; email?: string | null }) =>
    user.botUser && orderEmails.length > 0
      ? orderEmails.join(', ')
      : user.email;

  const renderNotificationRows = () => {
    if (isPrefsLoading && !notificationPreferences) {
      return (
        <div className="flex items-center justify-center py-6">
          <Spinner size="sm" aria-label="Loading notification preferences" />
        </div>
      );
    }

    if (notificationUsers.length === 0) {
      return (
        <p className="text-sm text-default-500 italic">
          No distributor recipients to configure. Add a distributor user to
          manage their order notifications.
        </p>
      );
    }

    return (
      <ul className="space-y-3">
        {notificationUsers.map((user) => {
          const isEnabled = !deselectedList.includes(user.id);
          return (
            <li
              key={user.id}
              className="flex items-center justify-between gap-4"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-foreground truncate">
                    {user.name || 'Unnamed user'}
                  </p>
                  {user.botUser && (
                    <Chip size="sm" variant="flat" color="primary">
                      Primary
                    </Chip>
                  )}
                </div>
                <div className="flex items-center gap-1.5 text-xs text-default-500">
                  <FiMail className="h-3 w-3 flex-shrink-0" />
                  {getRowEmail(user) ? (
                    <span className="truncate">{getRowEmail(user)}</span>
                  ) : (
                    <span className="italic">No email configured</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {updatingUserId === user.id && (
                  <Spinner size="sm" aria-label="Saving" />
                )}
                <Switch
                  isSelected={isEnabled}
                  onValueChange={(selected) =>
                    handleNotificationChange(user.id, selected)
                  }
                  isDisabled={isPrefsLoading || isUpdating}
                  size="sm"
                  aria-label={`Order notifications for ${
                    user.name || user.email || user.id
                  }`}
                />
              </div>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div className="space-y-6">
      {/* Supplier Info */}
      <div>
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <FiTruck className="w-4 h-4 text-default-400" />
            <p className="text-xs font-medium text-default-500 tracking-wide">
              Primary Distributor
            </p>
          </div>
          {canAssign && supplier && (
            <Button
              size="sm"
              variant="flat"
              color="primary"
              startContent={<FiEdit className="w-4 h-4" />}
              onPress={() => setIsAssignModalOpen(true)}
              data-testid="change-distributor-button"
            >
              Change Distributor
            </Button>
          )}
        </div>

        {supplier ? (
          <div
            className={`relative overflow-hidden rounded-xl border border-default-200 bg-content1 p-4 transition-all ${
              supplierProfileRoute
                ? 'group cursor-pointer hover:border-primary/50 hover:shadow-md'
                : ''
            }`}
            onClick={supplierProfileRoute ? handleSupplierClick : undefined}
            role={supplierProfileRoute ? 'button' : undefined}
            tabIndex={supplierProfileRoute ? 0 : undefined}
            onKeyDown={(e) => {
              if (!supplierProfileRoute) return;

              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleSupplierClick();
              }
            }}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-4">
                <Avatar
                  name={getInitials(supplier.name || 'D')}
                  getInitials={(value) => value}
                  className="h-12 w-12 text-lg shrink-0"
                  isBordered
                  color="primary"
                  radius="lg"
                />
                <div>
                  <h4 className="text-base font-semibold text-foreground group-hover:text-primary transition-colors">
                    {supplier.name}
                  </h4>
                  {supplier.referenceId && (
                    <p className="text-sm text-default-500">
                      ID:{' '}
                      <span className="font-mono">{supplier.referenceId}</span>
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Chip
                  color="success"
                  variant="flat"
                  size="sm"
                  className="capitalize"
                >
                  Active
                </Chip>
                {supplierProfileRoute && (
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <FiExternalLink className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-default-300 bg-default-50 p-6 text-center">
            <FiTruck className="mx-auto h-8 w-8 text-default-300 mb-2" />
            <p className="text-default-500">No distributor assigned</p>
            {canAssign && (
              <Button
                size="sm"
                color="primary"
                variant="flat"
                className="mt-3"
                startContent={<FiPlus className="w-4 h-4" />}
                onPress={() => setIsAssignModalOpen(true)}
                data-testid="assign-distributor-button"
              >
                Assign Distributor
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Notification Preferences — one order-transmission toggle per user */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <FiBell className="w-4 h-4 text-default-400" />
          <p className="text-xs font-medium text-default-500 tracking-wide">
            Order Notification Preferences
          </p>
        </div>

        <div className="rounded-xl border border-default-200 bg-content1 p-4">
          {renderNotificationRows()}
        </div>
      </div>

      {/* Distributor assignment (requested chats only, #123) */}
      {canAssign && (
        <EntitySearchModal
          isOpen={isAssignModalOpen}
          onClose={() => setIsAssignModalOpen(false)}
          title={supplier ? 'Change Distributor' : 'Assign Distributor'}
          searchPlaceholder="Search distributors by name..."
          entityType="distributor"
          isSearching={isSearchingSuppliers}
          searchResults={suppliers.map((option) => ({
            id: option.value,
            name: option.metaData?.name || option.label,
            label: option.label,
            subtitle: option.metaData?.referenceId || '',
          }))}
          onSearch={searchSuppliers}
          onConfirm={handleAssignSupplier}
          isConfirming={isAssigning}
        />
      )}
    </div>
  );
};
